import {
  AfterResponseHook,
  BeforeErrorHook,
  BeforeRequestHook,
  BeforeRetryHook,
  HTTPError,
  NormalizedOptions,
} from 'ky';

import * as authUtil from '@campaign/domain/middlewares/auth/utils';
import { getState, store } from '@campaign/domain/store';
import { setRefreshAccessToken } from '@campaign/domain/store/reducers';
import { createLogger } from '@campaign/utils/logger';

const logger = createLogger('utils/api');

/**
 * Log X-Request-Id of response
 *
 * @param _req Request
 * @param _options NormalizedOptions
 * @param res Response
 */
export const logRequestId: AfterResponseHook = async (
  _req: Request,
  _options: NormalizedOptions,
  res: Response
) => {
  const requestId = res.headers.get('X-Request-Id');

  if (requestId) {
    logger.info('X-Request-Id:', requestId);
  }
};

/**
 * Add correct headers with access token to the request headers
 *
 * @param request Request
 */
export const addHeadersAuthenticationHook: BeforeRequestHook = async (
  request: Request
) => {
  try {
    const accessToken = getState().loggedInUser.token ?? null;
    request.headers.set('Authorization', `Bearer ${accessToken}`);
    request.headers.set('Content-Type', `application/json`);
    request.headers.set('x-nb-fingerprint', `fingerprint`);
  } catch (error) {
    logger.error({ logData: error instanceof Error && error.message });
  }
};

/**
 * Obtain a new access token before retrying the request
 */
export const refreshTokenHook: BeforeRetryHook = async () => {
  try {
    const token = await authUtil.refreshAccessToken();

    if (token) {
      logger.info(token);
      setRefreshAccessToken(store, token);
    } else {
      throw new Error('Error refreshing token during hook');
    }
  } catch (error) {
    logger.error({ logData: error instanceof Error && error.message });
  }
};

/**
 * Enhances the error message of an HTTPError by attempting to parse
 * a more descriptive message from the server's JSON response
 *
 * @param error HTTPError
 * @returns HTTPError
 */
export const beforeErrorHook: BeforeErrorHook = async (error) => {
  if (error instanceof HTTPError) {
    try {
      if (error.response) {
        const data = await error.response.json();
        if (data?.message) {
          error.message = data.message;
        }
      }
    } catch (jsonError) {
      logger.warn('Failed to parse error response JSON', jsonError);
    }
  }

  return error;
};
